import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';
import {
  Controller,
  type ControllerFieldState,
  type ControllerProps,
  type ControllerRenderProps,
  type FieldPath,
  type FieldValues,
  FormProvider,
  type RegisterOptions,
  type SubmitHandler,
  type UseFormReturn,
} from 'react-hook-form';

import { cn } from '../../styles/lib/utils';
import { Field, FieldError, FieldLabelRow, type FieldLabelRowProps } from '../Field';

type FormProps<TFieldValues extends FieldValues, TTransformedValues = TFieldValues> = Omit<
  React.ComponentProps<'form'>,
  'onSubmit'
> & {
  form: UseFormReturn<TFieldValues, any, TTransformedValues>;
  onSubmit: SubmitHandler<TTransformedValues>;
};

/** `noValidate` keeps the browser's own validation out of submit so react-hook-form owns errors and focus. */
function Form<TFieldValues extends FieldValues, TTransformedValues = TFieldValues>({
  form,
  onSubmit,
  children,
  ...props
}: FormProps<TFieldValues, TTransformedValues>) {
  return (
    <FormProvider {...form}>
      <form noValidate onSubmit={form.handleSubmit(onSubmit)} {...props}>
        {children}
      </form>
    </FormProvider>
  );
}

type FormFieldIds = {
  id: string;
  labelId: string;
  messageId: string;
};

type FormFieldContextValue = FormFieldIds & {
  name: string;
  isRequired?: boolean;
  field: ControllerRenderProps<FieldValues, FieldPath<FieldValues>>;
  fieldState: ControllerFieldState;
};

const FormFieldContext = React.createContext<FormFieldContextValue | null>(null);

function useFormField() {
  const context = React.useContext(FormFieldContext);
  if (!context) {
    throw new Error('useFormField must be used inside a FormField');
  }
  return context;
}

type FormFieldRenderProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = FormFieldIds & {
  field: ControllerRenderProps<TFieldValues, TName>;
  fieldState: ControllerFieldState;
};

type FormFieldProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = Omit<
  ControllerProps<TFieldValues, TName>,
  'render' | 'rules'
> & {
  isRequired?: boolean;
  validate?: RegisterOptions<TFieldValues, TName>['validate'];
  rules?: Omit<RegisterOptions<TFieldValues, TName>, 'required' | 'validate'>;
  id?: string;
  render: (props: FormFieldRenderProps<TFieldValues, TName>) => React.ReactElement;
};

function FormField<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  isRequired,
  validate,
  rules,
  id: idProp,
  render,
  ...controllerProps
}: FormFieldProps<TFieldValues, TName>) {
  const generatedId = React.useId();
  const id = idProp ?? generatedId;
  const ids: FormFieldIds = { id, labelId: `${id}-label`, messageId: `${id}-message` };

  return (
    <Controller
      {...controllerProps}
      rules={{ ...rules, required: isRequired ? 'Required' : false, validate }}
      render={({ field, fieldState }) => (
        <FormFieldContext.Provider
          value={{
            ...ids,
            name: field.name,
            isRequired,
            field: field as unknown as FormFieldContextValue['field'],
            fieldState,
          }}
        >
          {render({ field, fieldState, ...ids })}
        </FormFieldContext.Provider>
      )}
    />
  );
}

function FormItem({ className, ...props }: React.ComponentProps<typeof Field>) {
  const { fieldState } = useFormField();
  return <Field data-invalid={fieldState.invalid} className={cn('gap-1', className)} {...props} />;
}

type FormLabelProps = FieldLabelRowProps;

function FormLabel(props: FormLabelProps) {
  const { id, labelId } = useFormField();
  return <FieldLabelRow id={labelId} htmlFor={id} {...props} />;
}

function FormControl(props: React.ComponentProps<typeof Slot>) {
  const { id, messageId, isRequired, field, fieldState } = useFormField();

  return (
    <Slot
      ref={field.ref}
      id={id}
      aria-required={isRequired || undefined}
      aria-invalid={fieldState.invalid || undefined}
      aria-describedby={fieldState.error?.message ? messageId : undefined}
      {...props}
    />
  );
}

function FormMessage({ className, children, ...props }: React.ComponentProps<typeof FieldError>) {
  const { messageId, fieldState } = useFormField();
  const content = children ?? fieldState.error?.message;

  if (!content) {
    return null;
  }

  return (
    <FieldError id={messageId} className={cn('text-xs', className)} {...props}>
      {content}
    </FieldError>
  );
}

export { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, useFormField };
export type { FormProps, FormFieldProps, FormFieldRenderProps, FormLabelProps };
